import { FC, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { ClockIcon } from '@/atoms/Icons/ClockIcon'
import { QrIcon } from '@/atoms/Icons/QrIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { Placeholder } from '@/atoms/Placeholder'
import { EyebrowSM, Text, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { GetTicketCardReleaseQuery, GetTicketCardReleaseQueryVariables } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { useTranslate } from '@/utils/translate/translate-client'
import { Card } from '@/views/AccountView/tabs/MyTicketsTab/components/Card'
import { EventCardPill } from '@/views/AccountView/tabs/MyTicketsTab/components/EventCardPill'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'

export const GET_TICKET_CARD_RELEASE = gql`
  query getTicketCardRelease($slug: String!) {
    theatricalRelease(input: { theatricalSlug: $slug }) {
      theatricalRelease {
        id
        verticalPoster
        movieDuration
        movieRating
        title
        theatricalSlug
        shortDescription
        images {
          backdrop {
            image16x9
          }
          nonTitleArt {
            image16x9
          }
        }
      }
    }
  }
`

interface Props {
  ticket: SimpleTicketItem
  isPastMovieTicket?: boolean
  className?: string
  disableSkeleton?: boolean
}

export const TicketCard: FC<Props> & { Skeleton: ReactFCC } = ({
  ticket,
  isPastMovieTicket,
  className,
  disableSkeleton = false,
}) => {
  const { t } = useTranslate('account')
  const { venueSummary, showtimeSummary, id, theatricalSlug, seatQuantity } = ticket ?? {}

  const { data, loading } = useQuery<GetTicketCardReleaseQuery, GetTicketCardReleaseQueryVariables>(
    GET_TICKET_CARD_RELEASE,
    { variables: { slug: theatricalSlug as string }, skip: !theatricalSlug, errorPolicy: 'all' },
  )
  const theatricalRelease = data?.theatricalRelease?.theatricalRelease
  const isStatusCanceled = ticket?.status?.reference === 'CANCELLED'

  const ticketLink = useMemo(() => {
    return `${paths.tickets.checkout.complete(theatricalSlug, id)}?route=account&order=edit`
  }, [id, theatricalSlug])

  const pillData = useMemo(() => {
    return [
      {
        name: showtimeSummary?.localStartDateFriendlyString,
        icon: <CalendarIcon color={isPastMovieTicket ? 'black' : 'white'} size={12} />,
      },
      {
        name: showtimeSummary?.localStartTimeFriendlyString,
        icon: <ClockIcon color={isPastMovieTicket ? 'black' : 'white'} size={12} />,
      },
      {
        name: t('ticketCardCountV2', {
          count: seatQuantity ?? 0,
          seatQuantity,
          defaultValue_one: '{{seatQuantity}} Ticket',
          defaultValue_other: '{{seatQuantity}} Tickets',
        }),
        icon: <TicketIcon color={isPastMovieTicket ? 'black' : 'white'} size={12} />,
      },
    ]
  }, [
    isPastMovieTicket,
    seatQuantity,
    showtimeSummary?.localStartDateFriendlyString,
    showtimeSummary?.localStartTimeFriendlyString,
    t,
  ])

  if (loading && !disableSkeleton) return <TicketCard.Skeleton />

  return (
    <Card
      className={classNames(className)}
      variant={isPastMovieTicket ? 'white' : 'image'}
      backgroundImg={isPastMovieTicket ? null : theatricalRelease?.images?.nonTitleArt?.image16x9}
      childrenWrapperClassName={classNames('flex flex-col', isPastMovieTicket && '')}
    >
      <FlexRow className="w-full !items-start gap-4">
        {theatricalRelease?.verticalPoster && isPastMovieTicket && (
          <Image
            style={{ aspectRatio: 2 / 3 }}
            className="shrink-0 rounded-lg md:w-[100px]"
            alt="poster"
            width={72}
            height={108}
            src={theatricalRelease.verticalPoster}
          />
        )}
        <div className="grid w-full">
          {isStatusCanceled && (
            <EyebrowSM
              weight="medium"
              color="error-700"
              className="ml-auto block w-fit rounded-[5px] bg-error-100 px-2.5 py-[3px]"
            >
              {t('canceled', 'Canceled')}
            </EyebrowSM>
          )}

          <FlexRow className="relative !items-start justify-between">
            <Text
              color={isPastMovieTicket ? 'black' : 'white'}
              weight="bold"
              className={classNames(
                'photon-title-sm md:photon-title-md mt-1 md:mt-2',
                !isPastMovieTicket && 'max-w-[50%]',
              )}
            >
              {theatricalRelease?.title}
            </Text>
            {!isPastMovieTicket && (
              <div className="size[64px] absolute right-0 top-0 rounded-lg bg-black/40">
                <QrIcon className="md:size-[64px]" size={48} color="white" />
              </div>
            )}
          </FlexRow>

          <FlexColumn className="mt-2 gap-0.5 md:mt-3">
            <Text
              className="photon-caption-xs md:photon-caption-sm"
              color={isPastMovieTicket ? 'core-gray-700' : 'core-gray-400'}
            >
              {t('theater', 'Theater')}
            </Text>
            <TitleXS color={isPastMovieTicket ? 'black' : 'white'} weight="semibold">
              {venueSummary?.name}
            </TitleXS>
          </FlexColumn>

          <FlexRow className="mt-2 flex-wrap gap-1 pb-6 pt-1 md:pt-2">
            {pillData?.map((pill) => {
              if (!pill?.name) return null

              return (
                <EventCardPill
                  variant={isPastMovieTicket ? 'outline' : 'dark'}
                  key={pill.name}
                  text={pill.name}
                  leftIcon={pill.icon}
                />
              )
            })}
          </FlexRow>
        </div>
      </FlexRow>

      {!isStatusCanceled && (
        <LinkButton
          href={ticketLink}
          className="mt-auto flex items-center gap-1 md:w-fit md:grow-0"
          variant={isPastMovieTicket ? 'lightgray' : 'white'}
        >
          {!isPastMovieTicket && <QrIcon size={20} color="black" />}
          {isPastMovieTicket ? t('viewDetails', 'View Details') : t('viewTickets', 'View Tickets')}
        </LinkButton>
      )}
    </Card>
  )
}

TicketCard.Skeleton = function TicketCardSkeleton() {
  return (
    <FlexColumn className="w-full max-w-[440px] justify-start gap-4 rounded-xl border border-core-gray-200 bg-white p-4 shadow-lg">
      <FlexRow className="gap-4">
        <Placeholder className="h-[122px] w-[82px] rounded-lg" />
        <FlexColumn className="gap-1">
          <Placeholder className="h-3 w-20" />
          <Placeholder className="h-3 w-28" />
          <Placeholder className="h-3 w-32" />
          <Placeholder className="h-3 w-32" />
        </FlexColumn>
      </FlexRow>
      <Placeholder className="h-10 w-full rounded-md" />
    </FlexColumn>
  )
}
